/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useState } from 'react';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import { withTheme } from '@mui/styles';
import { useFormik } from 'formik';
import { isNil, toNumber, toString } from 'lodash';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { FlightLegInfo } from '../../../shared/entities/flightLeg';
import { updateTakeoffTime } from '../../../store/flightLegs/actions';
import { formatTtho } from '../../../utils/DateTimeUtils';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../utils/StringUtils';
import FormModal from '../../common/modals/FormModal';
import appTheme from '../../common/theme/appTheme.json';
import NumericStringFormattedInput from '../../common/helpers/NumericStringFormattedInput';

const validationSchema = yup.object({
  hours: yup
    .number()
    .min(0, 'The HH value must be between 0-23')
    .max(23, 'The HH value must be between 0-23')
    .required('HH is required'),
  minutes: yup
    .number()
    .min(0, 'The MM value must be between 0-59')
    .max(59, 'The MM value must be between 0-59')
    .required('MM is required'),
});

// Styles
const flightInfoContentStyles = { color: appTheme.primaryTextColor, fontSize: '16px', lineHeight: '24px' };
const flightInfoHeaderStyles = { color: appTheme.mptFormLabelText, fontSize: '14px', lineHeight: '20px' };
const errorTextContainerStyles = { height: '21px' };
const timeInputStyles = {
  fontSize: '18px',
  '.MuiOutlinedInput-notchedOutline': {
    border: '1px solid #ffffff',
  },
  '.MuiInputBase-input': {
    textAlign: 'center',
  },
  '& .MuiOutlinedInput-root': {
    color: appTheme.primaryTextColor,
    fontSize: '18px',
    fontFamily: appTheme.appFontFamily,
    padding: 0,
    height: '40px',
    width: '55px',
    fontWeight: 700,
  },
};
const flightInfoContainerStyles = { marginRight: '40px' };

// Contstants
const ONE_HOUR_IN_MINUTES = 60;
const USER_SET_TAKEOFF_MIN_UNSET_VALUE = -1;
const MAX_HOURS = 23;
const MAX_MINUTES = 59;
const MIN_MUMERIC_LIMIT = 0;
const MAX_NUMERIC_LIMIT = 99;

export interface FlightLegTakeoffTimeModalProps {
  flightLeg: FlightLegInfo;
  missionPlanId: number | string | null;
  open: boolean;
  onClose: () => void;
}

const FlightLegTakeoffTimeModal = (props: FlightLegTakeoffTimeModalProps) => {
  const { flightLeg, missionPlanId, open, onClose } = props;
  const dispatch = useDispatch();

  const [shouldClear, setShouldClear] = useState<boolean>(false);

  const formik = useFormik({
    initialValues: {
      hours:
        flightLeg.userSetTakeoffTimeMin !== USER_SET_TAKEOFF_MIN_UNSET_VALUE
          ? Math.floor(flightLeg.userSetTakeoffTimeMin / ONE_HOUR_IN_MINUTES)
              .toString()
              .padStart(2, '0')
          : '',
      minutes:
        flightLeg.userSetTakeoffTimeMin !== USER_SET_TAKEOFF_MIN_UNSET_VALUE
          ? (flightLeg.userSetTakeoffTimeMin % ONE_HOUR_IN_MINUTES).toString().padStart(2, '0')
          : '',
    },
    validationSchema: !shouldClear ? validationSchema : undefined,

    onSubmit: (values: { hours: number | string | null; minutes: number | string | null }) => {
      const { hours, minutes } = values;
      if (!isNil(missionPlanId)) {
        if (shouldClear) {
          dispatch(
            updateTakeoffTime({
              flightLegId: toString(flightLeg.id),
              takeoffTimeNumberOfMinutes: USER_SET_TAKEOFF_MIN_UNSET_VALUE,
              missionPlanId: toNumber(missionPlanId),
            }),
          );
        } else {
          dispatch(
            updateTakeoffTime({
              flightLegId: toString(flightLeg.id),
              takeoffTimeNumberOfMinutes: toNumber(minutes || 0) + toNumber(hours || 0) * ONE_HOUR_IN_MINUTES,
              missionPlanId: toNumber(missionPlanId),
            }),
          );
        }
        onClose();
      }
    },
  });

  return (
    <FormModal
      open={open}
      onClose={onClose}
      onSubmit={formik.handleSubmit}
      submitDisabled={
        (!shouldClear && formik.touched.hours && !!formik.errors.hours) ||
        (!shouldClear && formik.touched.minutes && !!formik.errors.minutes)
      }
      closeText="Cancel"
      submitText="Save"
      title="Takeoff Time"
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          height: '100%',
          paddingX: '24px',
        }}
      >
        <Box
          sx={{
            width: '100%',
            color: appTheme.primaryTextColor,
            fontSize: '16px',
            lineHeight: '24px',
            marginTop: '-24px',
            marginBottom: '12px',
          }}
        >
          Add or update the takeoff time for Leg {flightLeg?.legOrder}
        </Box>
        <Box sx={{ width: '100%', minHeight: '42px', display: 'flex' }}>
          {(!shouldClear && formik.touched.hours && !!formik.errors.hours) ||
          (!shouldClear && formik.touched.minutes && !!formik.errors.minutes) ? (
            <ErrorRoundedIcon color="error" />
          ) : (
            <></>
          )}
          <Box>
            <Box>
              {!shouldClear && formik.touched.hours && !!formik.errors.hours ? (
                <Box sx={errorTextContainerStyles}>
                  <Typography variant="caption" color="error">
                    {formik.touched.hours && formik.errors.hours}
                  </Typography>
                </Box>
              ) : (
                <></>
              )}
            </Box>
            <Box>
              {!shouldClear && formik.touched.minutes && !!formik.errors.minutes ? (
                <Box sx={errorTextContainerStyles}>
                  <Typography variant="caption" color="error">
                    {formik.touched.minutes && formik.errors.minutes}
                  </Typography>
                </Box>
              ) : (
                <></>
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ flexGrow: 1, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box>
                  <IconButton
                    sx={{ color: appTheme.mptLightBlue }}
                    onClick={() => {
                      if (!isNil(formik.values.hours) && toNumber(formik.values.hours) < MAX_HOURS) {
                        formik.setFieldValue('hours', (toNumber(formik.values.hours) + 1).toString().padStart(2, '0'));
                      }
                    }}
                  >
                    <ExpandLessRoundedIcon />
                  </IconButton>
                </Box>
                <TextField
                  sx={timeInputStyles}
                  type="text"
                  variant="outlined"
                  placeholder="HH"
                  name="hours"
                  onFocus={formik.handleBlur}
                  value={formik.values.hours === '' ? '' : formik.values.hours.toString().padStart(2, '0')}
                  onChange={e => {
                    if (e?.target?.value !== '') {
                      setShouldClear(false);
                    }
                    formik.handleChange(e);
                  }}
                  InputProps={
                    {
                      autoComplete: 'off',
                      inputComponent: NumericStringFormattedInput as any,
                      inputProps: {
                        min: MIN_MUMERIC_LIMIT,
                        max: MAX_NUMERIC_LIMIT,
                      },
                    } as any
                  }
                  error={!shouldClear && formik.touched.hours && !!formik.errors.hours}
                />
                <Box>
                  <IconButton
                    sx={{ color: appTheme.mptLightBlue }}
                    onClick={() => {
                      if (!isNil(formik.values.hours) && toNumber(formik.values.hours) > 0) {
                        formik.setFieldValue('hours', (toNumber(formik.values.hours) - 1).toString().padStart(2, '0'));
                      }
                    }}
                  >
                    <ExpandMoreRoundedIcon />
                  </IconButton>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                color: appTheme.primaryTextColor,
                fontSize: '18px',
                lineHeight: '118px',
                marginX: '4px',
                fontWeight: 700,
              }}
            >
              :
            </Box>
            <Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box>
                  <IconButton
                    sx={{ color: appTheme.mptLightBlue }}
                    onClick={() => {
                      if (!isNil(formik.values.minutes) && toNumber(formik.values.minutes) < MAX_MINUTES) {
                        formik.setFieldValue(
                          'minutes',
                          (toNumber(formik.values.minutes) + 1).toString().padStart(2, '0'),
                        );
                      }
                    }}
                  >
                    <ExpandLessRoundedIcon />
                  </IconButton>
                </Box>
                <Box>
                  <TextField
                    sx={timeInputStyles}
                    type="text"
                    variant="outlined"
                    name="minutes"
                    placeholder="MM"
                    value={formik.values.minutes === '' ? '' : formik.values.minutes.toString().padStart(2, '0')}
                    onFocus={formik.handleBlur}
                    onChange={e => {
                      if (e?.target?.value !== '') {
                        setShouldClear(false);
                      }
                      formik.handleChange(e);
                    }}
                    InputProps={
                      {
                        autoComplete: 'off',
                        inputComponent: NumericStringFormattedInput as any,
                        inputProps: {
                          min: MIN_MUMERIC_LIMIT,
                          max: MAX_NUMERIC_LIMIT,
                        },
                      } as any
                    }
                    error={!shouldClear && formik.touched.minutes && !!formik.errors.minutes}
                  />
                </Box>
                <Box>
                  <IconButton
                    sx={{ color: appTheme.mptLightBlue }}
                    onClick={() => {
                      if (!isNil(formik.values.minutes) && toNumber(formik.values.minutes) > 0) {
                        formik.setFieldValue(
                          'minutes',
                          (toNumber(formik.values.minutes) - 1).toString().padStart(2, '0'),
                        );
                      }
                    }}
                  >
                    <ExpandMoreRoundedIcon />
                  </IconButton>
                </Box>
              </Box>
            </Box>
            <Box sx={{ height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconButton
                color="secondary"
                onClick={() => {
                  setShouldClear(true);
                  formik.setFieldValue('hours', '');
                  formik.setFieldValue('minutes', '');
                }}
              >
                <ClearRoundedIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', marginBottom: '24px' }}>
          <Box sx={flightInfoContainerStyles}>
            <Box sx={flightInfoHeaderStyles}>Departure</Box>
            <Box sx={flightInfoContentStyles}>{flightLeg?.origin || EMPTY_FIELD_STRING_PLACEHOLDER}</Box>
          </Box>
          <Box sx={flightInfoContainerStyles}>
            <Box sx={flightInfoHeaderStyles}>Flight Duration</Box>
            <Box sx={flightInfoContentStyles}>
              {flightLeg?.durationMin
                ? formatTtho(flightLeg.durationMin * ONE_HOUR_IN_MINUTES)
                : EMPTY_FIELD_STRING_PLACEHOLDER}
            </Box>
          </Box>
          <Box sx={flightInfoContainerStyles}>
            <Box sx={flightInfoHeaderStyles}>Arrival</Box>
            <Box sx={flightInfoContentStyles}>{flightLeg?.destination || EMPTY_FIELD_STRING_PLACEHOLDER}</Box>
          </Box>
        </Box>
      </Box>
    </FormModal>
  );
};
export default withTheme(FlightLegTakeoffTimeModal);
